import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  AlertTitle,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  Gavel as GavelIcon,
  DirectionsCar as DirectionsCarIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  People as PeopleIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as AccessTimeIcon,
  LocalGasStation as LocalGasStationIcon,
  Speed as SpeedIcon,
  Palette as PaletteIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { auctionServices } from "../../services/AllServices";
import { IMAGE_BASE } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import "../../styles/custom.css";

const ViewAuction = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bidApprovalDialog, setBidApprovalDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReasonDialog, setCancelReasonDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedBid, setSelectedBid] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (id) {
      fetchAuctionData();
    }
  }, [id]);

  // Real-time countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchAuctionData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await auctionServices.getAuctionById({ id });
      setAuction(response?.data?.data?.auction || response?.data?.data);
    } catch (error) {
      setError(error.message);
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBid = async () => {
    try {
      await auctionServices.approveBid({ id: selectedBid._id });
      setBidApprovalDialog(false);
      setSelectedBid(null);
      fetchAuctionData();
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleRejectBid = async () => {
    try {
      await auctionServices.rejectBid({ id: selectedBid._id });
      setBidApprovalDialog(false);
      setSelectedBid(null);
      fetchAuctionData();
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleCancelAuction = () => {
    setCancelDialog(true);
  };

  const confirmCancelAuction = () => {
    setCancelDialog(false);
    setCancelReasonDialog(true);
  };

  const submitCancelWithReason = async () => {
    if (!cancelReason.trim()) {
      setError("Please provide a reason for cancelling the auction");
      return;
    }
    
    try {
      await auctionServices.cancelAuction({ 
        id, 
        reason: cancelReason.trim() 
      });
      setCancelReasonDialog(false);
      setCancelReason("");
      navigate("/event-management");
    } catch (error) {
      errorHandler(error);
    }
  };

  const getStatusChip = (auction) => {
    if (!auction) return null;

    const now = new Date();
    const startDate = new Date(auction.schedule?.startTime);
    const endDate = new Date(auction.schedule?.endTime);

    if (auction.status === 0) {
      return <Chip label="Inactive" color="default" size="small" />;
    }

    if (auction.auctionStatus == "Cancelled") {
      return <Chip label="Cancelled" color="error" size="small" />;
    }

    if (now < startDate) {
      return <Chip label="Upcoming" color="info" size="small" />;
    } else if (now >= startDate && now <= endDate) {
      return <Chip label="Live" color="success" size="small" />;
    } else {
      return <Chip label="Completed" color="default" size="small" />;
    }
  };

  const getAuctionTypeChip = (type) => {
    const typeConfig = {
      "normal": { label: "Normal Auction", color: "primary", icon: <TrendingUpIcon /> },
      "marketDropAi": { label: "MarketDropAI Auction", color: "secondary", icon: <TrendingDownIcon /> },
      "claimPoint": { label: "ClaimPoint Auction", color: "success", icon: <GavelIcon /> },
    };
    
    const config = typeConfig[type] || { label: type, color: "default", icon: <GavelIcon /> };
    return (
      <Chip 
        label={config.label} 
        color={config.color} 
        size="small" 
        icon={config.icon}
      />
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (endDate) => {
    const now = currentTime; // Use currentTime state for real-time updates
    const end = new Date(endDate);
    const diff = end - now;

    if (diff <= 0) return "Ended";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <Box className="view-auction-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="view-auction-error-container">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!auction) {
    return (
      <Box className="view-auction-error-container">
        <Alert severity="info">Auction not found</Alert>
      </Box>
    );
  }

  const vehicle = auction.vehicle;

  return (
    <Box className="addedit-user-container">
      {/* Page Header Section */}
      <Box className="page-header-section">
        <Box className="page-header">
          <GavelIcon color="primary" />
          <Typography variant="h4" component="h1" className="page-title">
          Event Details
        </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" className="page-subtitle">
          View comprehensive event information, bidding history, and management options
        </Typography>
      </Box>

      {/* Action Buttons Section */}
      <Box className="section-wrapper">
        <Card elevation={1} className="section-card">
          <CardContent>
            <Box className="view-auction-actions">
              {auction.auctionStatus == "Upcoming" && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/event-management/edit/${id}`)}
                  className="cancel-button"
                >
                  Edit Auction
                </Button>
              )}
              {auction.auctionStatus !== "Cancelled" && auction.auctionStatus !== "Completed" && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelAuction}
                  className="cancel-button"
                >
                  Cancel Event
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/event-management")}
                className="submit-button"
              >
                Back to List
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <br/>

      <Box className="section-wrapper">
        <Card elevation={1} className="section-card">
          <CardHeader
            title={
              <Box className="section-header">
                <TimerIcon color="primary" />
                <Typography variant="h5" className="section-title">
                Event Status & Progress
                </Typography>
              </Box>
            }
          />
        <CardContent>
            <Box className="view-auction-status-header">
            <Stack direction="row" spacing={2} alignItems="center">
              {getStatusChip(auction)}
              {getAuctionTypeChip(auction.auctionType)}
            </Stack>
          </Box>
      
          <Grid container spacing={2}>
            {auction.auctionStatus == "Cancelled" ? (
              <>
                <Grid item xs={12} md={6}>
                  <Box className="view-auction-status-card view-auction-status-card-error">
                    <CancelIcon color="error" className="view-auction-status-icon" />
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-status-label">
                      Event Status
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="error.main" className="view-auction-status-value">
                      Cancelled
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="view-auction-status-card view-auction-status-card-warning">
                    <AccessTimeIcon color="warning" className="view-auction-status-icon" />
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-status-label">
                      Cancelled On
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="warning.main" className="view-auction-status-value">
                      {formatDate(auction.schedule?.endTime)}
                    </Typography>
                  </Box>
                </Grid>
                {auction.cancellationReason && (
                  <Grid item xs={12}>
                    <Box className="view-auction-cancellation-reason">
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        Cancellation Reason
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: "0.95rem", lineHeight: 1.6 }}>
                        {auction.cancellationReason}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12} md={4}>
                  <Box className="view-auction-status-card view-auction-status-card-grey">
                    <CalendarTodayIcon color="action" className="view-auction-status-icon" />
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-status-label">
                      Started
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" className="view-auction-status-value">
                      {formatDate(auction.schedule?.startTime)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box className="view-auction-status-card view-auction-status-card-grey">
                    <AccessTimeIcon color="action" className="view-auction-status-icon" />
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-status-label">
                      Ends
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" className="view-auction-status-value">
                      {formatDate(auction.schedule?.endTime)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box className="view-auction-status-card view-auction-status-card-primary">
                    <TimerIcon color="primary" className="view-auction-timer-icon" />
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-status-label">
                      Time Remaining
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary" className="view-auction-status-value">
                      {getTimeRemaining(auction.schedule?.endTime)}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
      </Box>
      <br />

        {/* Auction Details Section */}
        <Box className="section-wrapper section-content">
          <Card elevation={1} className="section-card">
           
            <CardContent >
              <Stack spacing={3}>

                <Box className="view-auction-description-container">
                  <Typography variant="body2" className="view-auction-description-label">
                    Description
                  </Typography>
                  <Typography variant="body1" className="view-auction-description-text">
                    {auction.description || "No description available"}
                  </Typography>
                </Box>

                {/* Default Price Drop Settings */}
                {(auction.priceDropRate || auction.defaultPriceDropIntervalUnit || auction.defaultPriceDropValue) && (
                  <Box className="view-auction-price-drop-section">
                    <Divider sx={{ mb: 2 }}>
                      <Chip label="Default Price Drop Settings" color="info" />
                    </Divider>
                    <Grid container spacing={2}>
                      {auction.defaultPriceDropIntervalValue && (
                        <Grid item xs={12} md={4}>
                          <Box className="view-auction-price-drop-card">
                            <Box className="view-auction-price-drop-header">
                              <TrendingDownIcon color="info" />
                              <Typography variant="subtitle2" className="view-auction-price-drop-title">
                                Price Drop Interval Value
                              </Typography>
                            </Box>
                            <Typography variant="h6" className="view-auction-price-drop-value">
                              {auction.defaultPriceDropIntervalValue}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {auction.defaultPriceDropIntervalUnit && (
                        <Grid item xs={12} md={4}>
                          <Box className="view-auction-price-drop-card">
                            <Box className="view-auction-price-drop-header">
                              <AccessTimeIcon color="info" />
                              <Typography variant="subtitle2" className="view-auction-price-drop-title">
                                Price Drop Interval Unit
                              </Typography>
                            </Box>
                            <Typography variant="h6" className="view-auction-price-drop-value">
                              {capitalize(auction.defaultPriceDropIntervalUnit)}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {auction.priceDropRate && (
                        <Grid item xs={12} md={4}>
                          <Box className="view-auction-price-drop-card">
                            <Box className="view-auction-price-drop-header">
                              <AttachMoneyIcon color="info" />
                              <Typography variant="subtitle2" className="view-auction-price-drop-title">
                                Price Drop Value
                              </Typography>
                            </Box>
                            <Typography variant="h6" className="view-auction-price-drop-value">
                              {auction.priceDropRate}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* ClaimPOINT Specific Information */}
                {auction.auctionType === "claimPoint" && (
                  <Box className="view-auction-claimpoint-section">
                    <Divider sx={{ mb: 2 }}>
                      <Chip label="ClaimPOINT Auction Details" color="success" />
                    </Divider>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box className="view-auction-claimpoint-card-secondary">
                          <Box className="view-auction-claimpoint-header">
                            <TrendingDownIcon color="secondary" />
                            <Typography variant="subtitle2" color="secondary.main" className="view-auction-claimpoint-title">
                              Consumer Price (MarketDropAI)
                            </Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="secondary.main" className="view-auction-claimpoint-value">
                            {formatCurrency(auction.claimPointConsumerPrice || auction.marketDropPrice)}
                          </Typography>
                          <Typography variant="caption" className="view-auction-claimpoint-caption">
                            Falling price event for consumers - Primary action: Claim Now
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box className="view-auction-claimpoint-card-primary">
                          <Box className="view-auction-claimpoint-header">
                            <TrendingUpIcon color="primary" />
                            <Typography variant="subtitle2" color="primary.main" className="view-auction-claimpoint-title">
                              Dealer Price (Normal Auction)
                            </Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="primary.main" className="view-auction-claimpoint-value">
                            {formatCurrency(auction.claimpointDealerPrice || auction.startingBid)}
                          </Typography>
                          <Typography variant="caption" className="view-auction-claimpoint-caption">
                            Ascending bid auction for dealers - Primary action: Place Bid
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>

      
      <br/>
      {/* Main Content */}
      <Box className="form-sections-container">
      {/* ClaimPOINT Special Information */}
        {auction.auctionType === "ClaimPoint" && (
          <Box className="section-wrapper">
            <Alert severity="info" className="view-auction-alert">
              <AlertTitle>ClaimPOINT Hybrid Auction</AlertTitle>
              This auction runs both Normal (Ascending) and MarketDropAI (Descending) formats simultaneously:
              <Box className="view-auction-alert-content">
                <Typography variant="body2">
                  • <strong>Consumers:</strong> See MarketDropAI (falling price) with "Claim Now" action
                </Typography>
                <Typography variant="body2">
                  • <strong>Dealers:</strong> See Normal (ascending price) with "Place Bid" action
                </Typography>
              </Box>
            </Alert>
          </Box>
        )}
        {/* Vehicle Information Section */}
        <Box className="section-wrapper">
          <Card elevation={1} className="section-card">
            <CardHeader
              title={
                <Box className="section-header">
                <DirectionsCarIcon color="primary" />
                  <Typography variant="h5" className="section-title">
                Vehicle Information
              </Typography>
                </Box>
              }
            />
            <CardContent>
              {vehicle?.vehicleImage?.[0] && (
                <Box className="view-auction-vehicle-image-container">
                  <CardMedia
                    component="img"
                    height="250"
                    image={IMAGE_BASE + vehicle.vehicleImage[0]}
                    alt={`${vehicle.make?.name} ${vehicle.model}`}
                    className="view-auction-vehicle-image"
                  />
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">Make</Typography>
                    <Typography variant="h6" className="view-auction-vehicle-value">{vehicle?.make?.name}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">Model</Typography>
                    <Typography variant="h6" className="view-auction-vehicle-value">{vehicle?.model}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">Year</Typography>
                    <Typography variant="h6" className="view-auction-vehicle-value">{vehicle?.year}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">Odometer</Typography>
                    <Typography variant="h6" className="view-auction-vehicle-value">{vehicle?.miles} {vehicle?.odometerUnit}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">Fuel Type</Typography>
                    <Typography variant="h6" className="view-auction-vehicle-value">{vehicle?.fuelType}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">Transmission</Typography>
                    <Typography variant="h6" className="view-auction-vehicle-value">{vehicle?.transmission}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">Interior Color</Typography>
                    <Typography variant="h6" className="view-auction-vehicle-value">{vehicle?.interiorColor}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">Exterior Color</Typography>
                    <Typography variant="h6" className="view-auction-vehicle-value">{vehicle?.exteriorColor}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className="view-auction-vehicle-card">
                    <Typography variant="body2" color="text.secondary" gutterBottom className="view-auction-vehicle-label">VIN</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ wordBreak: "break-all", fontSize: "0.9rem" }}>{vehicle?.VIN}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
     
      </Box>

      {/* Bid Approval Dialog */}
      <Dialog open={bidApprovalDialog} onClose={() => setBidApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bid Approval</DialogTitle>
        <DialogContent>
          {selectedBid && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Bid Amount:</strong> {formatCurrency(selectedBid.amount)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Bidder:</strong> {selectedBid.bidder?.name || selectedBid.bidder?.email}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Bid Time:</strong> {formatDate(selectedBid.createdAt)}
              </Typography>
              <Typography variant="body1">
                <strong>Status:</strong> {selectedBid.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBidApprovalDialog(false)}>Cancel</Button>
          <Button onClick={handleRejectBid} color="error">Reject</Button>
          <Button onClick={handleApproveBid} color="success" variant="contained">Approve</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Auction Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
        <DialogTitle>Cancel Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this event? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>No</Button>
          <Button onClick={confirmCancelAuction} color="warning" variant="contained">
            Yes, Cancel Event
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Reason Input Dialog */}
      <Dialog 
        open={cancelReasonDialog} 
        onClose={() => {
          setCancelReasonDialog(false);
          setCancelReason("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            Cancel Event - Reason Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for cancelling this event. This information will be recorded and may be shared with bidders.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Cancellation Reason"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter the reason for cancelling this event..."
            error={!cancelReason.trim() && cancelReason.length > 0}
            helperText={!cancelReason.trim() && cancelReason.length > 0 ? "Please provide a valid reason" : "Minimum 10 characters recommended"}
          />
          {auction && (
            <Box className="view-auction-dialog-details">
              <Typography variant="subtitle2" gutterBottom>
                Event Details:
              </Typography>
              <Typography variant="body2">
                <strong>Vehicle:</strong> {auction.vehicle?.make?.name} {auction.vehicle?.model}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {capitalize(auction.auctionType)}
              </Typography>
              <Typography variant="body2">
                <strong>Starting Bid:</strong> {formatCurrency(auction.startingBid || 0)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCancelReasonDialog(false);
              setCancelReason("");
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={submitCancelWithReason} 
            color="warning" 
            variant="contained"
            disabled={!cancelReason.trim()}
          >
            Submit Cancellation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewAuction;